/**
 * useTellerPicks — the per-room "who's talking" state for a group room (Step 11b, D3, D4).
 *
 * Owns two things the pane and the composer share:
 *   - the circle state per twin, derived from the saved `activeParticipants` and the picks;
 *   - the picks for the next send (persona tokens, the twins asked next), from the @ picker
 *     or an @tag.
 *
 * A circle tap toggles In <-> Out and saves `activeParticipants` through `onSettingsChange`
 * (optimistic in useChatRoom). A picked circle shows the picked ring; tapping it drops the pick
 * and saves Out. A pick is local: it rides the next send as `tellerPicks`, then sits in a
 * "sent, pending" set (ring still shown, never handed to a later send) until that send's
 * outcome arrives in `sendOutcomes`: accepted clears it, failed (removed, trial cap) gives it
 * back, an edit moves it to the item's new id. A blocked item keeps it pending for its retry.
 *
 * Two gates: `canUpdateSettings` lets a viewer tap circles at all (owner only, every tap is a
 * save); `canPickTellers` lets a viewer pick twins through the @ picker (anyone who can send,
 * like an @tag).
 *
 * Keep (the composer's @ picker, "Keep for next messages"): with keep on, a send's picks
 * stay takeable as `circle` picks after it is taken, so the next send carries them again.
 * The kept set lives in this browser only (localStorage `room-teller-keep:<roomToken>`,
 * per viewer), is written when a send takes it, and is read back once per room after the
 * participants load, dropping twins that left the room or are not ready.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ChatRoomParticipantDto,
  ChatRoomSettings,
} from '@digital-twins/shared-types'

export type TwinCircleState = 'off' | 'bidder' | 'picked'

/** Where a pick came from: a circle tap, a live @tag in the composer, or one a failed send gave back. */
type PickSource = 'circle' | 'tag' | 'held'

export interface MentionUnlinkRequest {
  /** Participant id (the persona token), the mention id the composer tracks. */
  participantId: string
  nonce: number
}

export interface TellerPicksController {
  /** Circle state per persona token. */
  states: Record<string, TwinCircleState>
  /** Persona tokens picked for the next send (the twins asked next), room order. */
  picks: Array<string>
  /** Twins in the next round (saved active set plus tag-picked twins). */
  bidderCount: number
  isAllOff: boolean
  canUpdateSettings: boolean
  /** The viewer may pick twins through the @ picker (circle taps still need canUpdateSettings). */
  canPickTellers: boolean
  cycle: (personaId: string) => void
  /** Composer: ids (participant ids, equal to persona tokens) of the @mentions linked in the text. */
  onMentionsChange: (participantIds: Array<string>) => void
  /** Composer: a mention the pane asked to unlink (text kept). */
  mentionUnlinkRequest: MentionUnlinkRequest | null
  /** Called when a message is queued; returns the picks it carries and marks them sent, pending. */
  takeSendPicks: () => Array<string>
  /** Binds the picks just taken to the queued send, so its outcome clears or restores exactly those. */
  markSent: (clientRequestId: string | undefined, picks: Array<string>) => void
  /**
   * The @ picker's uncheck: drops one twin's takeable pick (a tag pick also unlinks its
   * mention, text kept). A twin with no pick gets a local `circle` pick when it is ready.
   * Never saves and never touches another twin or a sent, pending pick.
   */
  togglePick: (personaId: string) => void
  /** "Keep for next messages": a send's picks stay picked for the next sends (this browser only). */
  isKeepOn: boolean
  setKeepOn: (isOn: boolean) => void
}

/** What the keep store holds per room, in this browser. */
export interface StoredTellerKeep {
  isOn: boolean
  personaIds: Array<string>
}

const KEEP_KEY_PREFIX = 'room-teller-keep:'
const KEEP_OFF: StoredTellerKeep = { isOn: false, personaIds: [] }

/** The kept picks for `roomToken`, or off when absent, unreadable or malformed. */
export function readTellerKeep(roomToken: string | null): StoredTellerKeep {
  if (!roomToken) {
    return KEEP_OFF
  }
  try {
    const raw = window.localStorage.getItem(`${KEEP_KEY_PREFIX}${roomToken}`)
    if (!raw) {
      return KEEP_OFF
    }
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return KEEP_OFF
    }
    const record = parsed as { isOn?: unknown; personaIds?: unknown }
    const personaIds = Array.isArray(record.personaIds)
      ? record.personaIds.filter((id): id is string => typeof id === 'string')
      : []
    return { isOn: record.isOn === true, personaIds }
  } catch {
    return KEEP_OFF
  }
}

/** Stores the kept picks; a storage failure is ignored (keep is a convenience). */
export function writeTellerKeep(roomToken: string | null, keep: StoredTellerKeep): void {
  if (!roomToken) {
    return
  }
  try {
    window.localStorage.setItem(`${KEEP_KEY_PREFIX}${roomToken}`, JSON.stringify(keep))
  } catch {
    // Storage unavailable (private window, blocked site data): keep lasts this page only.
  }
}

interface UseTellerPicksArgs {
  /** The room the picks belong to; a new token starts from no picks (ChatPage stays mounted across rooms). */
  roomToken?: string | null
  participants: Array<ChatRoomParticipantDto>
  settings: ChatRoomSettings | undefined
  /** Circle taps (Off <-> In saves). Owner only. */
  canUpdateSettings: boolean
  /** Local picks from the @ picker; anyone who can send. Defaults to canUpdateSettings. */
  canPickTellers?: boolean
  onSettingsChange?: (partial: Partial<ChatRoomSettings>) => void | Promise<void>
  /** Send outcomes in arrival order (useChatRoom); accepted clears a send's picks, failed restores them. */
  sendOutcomes?: ReadonlyArray<SendOutcome>
}

/** Saved active set, filtered to current participants, room order. Missing key = everyone. */
export function resolveActivePersonaIds(
  participants: Array<ChatRoomParticipantDto>,
  settings: ChatRoomSettings | undefined,
): Array<string> {
  const saved = settings?.activeParticipants
  if (!saved) {
    return participants.map((p) => p.personaId)
  }
  const savedSet = new Set(saved)
  return participants.filter((p) => savedSet.has(p.personaId)).map((p) => p.personaId)
}

/** The state a tap moves a circle to: Out -> In, anything else -> Out. */
export function nextCircleState(state: TwinCircleState): TwinCircleState {
  if (state === 'off') {
    return 'bidder'
  }
  return 'off'
}

function orderByRoom(participants: Array<ChatRoomParticipantDto>, ids: Set<string>): Array<string> {
  return participants.filter((p) => ids.has(p.personaId)).map((p) => p.personaId)
}

/** One send's outcome, in arrival order (useChatRoom `sendOutcomes`). */
export interface SendOutcome {
  clientRequestId: string
  /** rekeyed: an edited queued item now sends as `clientRequestId` (was `previousClientRequestId`). */
  outcome: 'accepted' | 'failed' | 'rekeyed'
  previousClientRequestId?: string
}

interface PicksState {
  /** Takeable picks for the next send. */
  sources: Record<string, PickSource>
  /** Sent, pending: persona token -> the clientRequestId (or take key) of the send carrying it. */
  pending: Record<string, string>
  /** Picks a failed send gave back: persona token -> that send's clientRequestId. */
  restoredFrom: Record<string, string>
}

const EMPTY_PICKS: PicksState = { sources: {}, pending: {}, restoredFrom: {} }

function withoutKeys<T>(record: Record<string, T>, keys: Iterable<string>): Record<string, T> {
  const next = { ...record }
  for (const key of keys) {
    delete next[key]
  }
  return next
}

/**
 * Applies one send outcome to the picks: accepted clears its picks, failed (the item left the
 * queue unsent) gives them back, rekeyed moves them to the edited item's new id. A blocked item
 * sends no outcome, so its picks stay pending and no later send can take them.
 */
export function applySendOutcome(state: PicksState, outcome: SendOutcome): PicksState {
  if (outcome.outcome === 'rekeyed') {
    const previous = outcome.previousClientRequestId
    const moved = Object.keys(state.pending).filter((personaId) => state.pending[personaId] === previous)
    if (!previous || moved.length === 0) {
      return state
    }
    const pending = { ...state.pending }
    for (const personaId of moved) {
      pending[personaId] = outcome.clientRequestId
    }
    return { ...state, pending }
  }
  const owned = Object.keys(state.pending).filter(
    (personaId) => state.pending[personaId] === outcome.clientRequestId,
  )
  if (outcome.outcome === 'accepted') {
    const restored = Object.keys(state.restoredFrom).filter(
      (personaId) =>
        state.restoredFrom[personaId] === outcome.clientRequestId && state.sources[personaId] === 'held',
    )
    if (owned.length === 0 && restored.length === 0) {
      return state
    }
    return {
      sources: withoutKeys(state.sources, restored),
      pending: withoutKeys(state.pending, owned),
      restoredFrom: withoutKeys(state.restoredFrom, restored),
    }
  }
  if (owned.length === 0) {
    return state
  }
  const sources = { ...state.sources }
  const restoredFrom = { ...state.restoredFrom }
  for (const personaId of owned) {
    if (!sources[personaId]) {
      sources[personaId] = 'held'
      restoredFrom[personaId] = outcome.clientRequestId
    }
  }
  return { sources, pending: withoutKeys(state.pending, owned), restoredFrom }
}

export function useTellerPicks(args: UseTellerPicksArgs): TellerPicksController {
  const {
    roomToken = null,
    participants,
    settings,
    canUpdateSettings,
    canPickTellers = canUpdateSettings,
    onSettingsChange,
    sendOutcomes,
  } = args

  // State stamped with another room's token reads as empty and is replaced on the next
  // write, so a pick held in room A never renders in room B, not even for one frame.
  const [stored, setStored] = useState<{ roomToken: string | null; picks: PicksState }>({
    roomToken,
    picks: EMPTY_PICKS,
  })
  const storedRef = useRef(stored)
  storedRef.current = stored
  const [unlinkState, setUnlinkState] = useState<{
    roomToken: string | null
    request: MentionUnlinkRequest | null
  }>({ roomToken, request: null })
  const unlinkNonceRef = useRef(0)
  const takeNonceRef = useRef(0)
  const lastTakeKeyRef = useRef<{ roomToken: string | null; key: string } | null>(null)
  const processedOutcomesRef = useRef<{ roomToken: string | null; count: number }>({
    roomToken,
    count: 0,
  })

  // Keep: read from storage once per room; a toggle overrides it for that room.
  const storedKeep = useMemo(() => readTellerKeep(roomToken), [roomToken])
  const [keepOverride, setKeepOverride] = useState<{ roomToken: string | null; isOn: boolean } | null>(null)
  const isKeepOn = keepOverride && keepOverride.roomToken === roomToken ? keepOverride.isOn : storedKeep.isOn
  const isKeepOnRef = useRef(isKeepOn)
  isKeepOnRef.current = isKeepOn
  const hydratedRoomRef = useRef<string | null>(null)

  const picksState = stored.roomToken === roomToken ? stored.picks : EMPTY_PICKS
  const pickSources = picksState.sources
  const pendingPicks = picksState.pending
  const mentionUnlinkRequest = unlinkState.roomToken === roomToken ? unlinkState.request : null

  /** Every write goes through here so takeSendPicks sees the latest state synchronously. */
  const updatePicks = useCallback(
    (fn: (prev: PicksState) => PicksState) => {
      const current = storedRef.current
      const isSameRoom = current.roomToken === roomToken
      const prev = isSameRoom ? current.picks : EMPTY_PICKS
      const next = fn(prev)
      if (next === prev && isSameRoom) {
        return
      }
      const nextStored = { roomToken, picks: next }
      storedRef.current = nextStored
      setStored(nextStored)
    },
    [roomToken],
  )

  const setMentionUnlinkRequest = useCallback(
    (request: MentionUnlinkRequest) => setUnlinkState({ roomToken, request }),
    [roomToken],
  )

  const readPicks = useCallback(
    () => (storedRef.current.roomToken === roomToken ? storedRef.current.picks : EMPTY_PICKS),
    [roomToken],
  )

  const activePersonaIds = useMemo(
    () => resolveActivePersonaIds(participants, settings),
    [participants, settings],
  )

  const picks = useMemo(
    () => orderByRoom(participants, new Set(Object.keys(pickSources))),
    [participants, pickSources],
  )

  const states = useMemo(() => {
    const active = new Set(activePersonaIds)
    const out: Record<string, TwinCircleState> = {}
    for (const p of participants) {
      // A sent pick keeps its ring until Nest accepts that send.
      out[p.personaId] = pickSources[p.personaId] || pendingPicks[p.personaId]
        ? 'picked'
        : active.has(p.personaId)
          ? 'bidder'
          : 'off'
    }
    return out
  }, [participants, activePersonaIds, pickSources, pendingPicks])

  const bidderCount = useMemo(
    () => new Set([...activePersonaIds, ...picks, ...Object.keys(pendingPicks)].filter(
      (personaId) => participants.some((p) => p.personaId === personaId),
    )).size,
    [activePersonaIds, picks, pendingPicks, participants],
  )

  // Accepted sends clear exactly the picks they carried; failed ones give them back.
  useEffect(() => {
    const outcomes = sendOutcomes ?? []
    // A new room or a shorter list is a fresh one (useChatRoom resets it on a room change).
    const processed = processedOutcomesRef.current
    const start = processed.roomToken === roomToken && processed.count <= outcomes.length
      ? processed.count
      : 0
    processedOutcomesRef.current = { roomToken, count: outcomes.length }
    const fresh = outcomes.slice(start)
    if (fresh.length === 0) {
      return
    }
    updatePicks((prev) => fresh.reduce(applySendOutcome, prev))
  }, [sendOutcomes, roomToken, updatePicks])

  // Kept picks come back once per room, after the participants load: twins that left the
  // room or are not ready are dropped (the server would drop a not-ready pick anyway).
  useEffect(() => {
    if (!roomToken || participants.length === 0 || hydratedRoomRef.current === roomToken) {
      return
    }
    hydratedRoomRef.current = roomToken
    if (!storedKeep.isOn || storedKeep.personaIds.length === 0) {
      return
    }
    const kept = new Set(storedKeep.personaIds)
    const ids = participants
      .filter((p) => kept.has(p.personaId) && p.enrichmentStatus === 'ready')
      .map((p) => p.personaId)
    if (ids.length === 0) {
      return
    }
    updatePicks((prev) => {
      const sources = { ...prev.sources }
      for (const personaId of ids) {
        if (!sources[personaId]) {
          sources[personaId] = 'circle'
        }
      }
      return { ...prev, sources }
    })
  }, [roomToken, participants, storedKeep, updatePicks])

  const saveActive = useCallback(
    (ids: Set<string>) => {
      void onSettingsChange?.({ activeParticipants: orderByRoom(participants, ids) })
    },
    [onSettingsChange, participants],
  )

  const cycle = useCallback(
    (personaId: string) => {
      // Every circle tap is a save: a viewer who cannot save leaves the circle as it is.
      if (!canUpdateSettings) {
        return
      }
      const participant = participants.find((p) => p.personaId === personaId)
      if (!participant) {
        return
      }
      const next = nextCircleState(states[personaId])
      const active = new Set(activePersonaIds)
      if (next === 'bidder') {
        active.add(personaId)
        saveActive(active)
        return
      }
      // -> off
      if (pickSources[personaId] === 'tag') {
        unlinkNonceRef.current += 1
        setMentionUnlinkRequest({ participantId: participant.id, nonce: unlinkNonceRef.current })
      }
      updatePicks((prev) => ({
        sources: withoutKeys(prev.sources, [personaId]),
        pending: withoutKeys(prev.pending, [personaId]),
        restoredFrom: withoutKeys(prev.restoredFrom, [personaId]),
      }))
      // A tag-picked twin that was never saved In has nothing to save.
      if (active.has(personaId)) {
        active.delete(personaId)
        saveActive(active)
      }
    },
    [canUpdateSettings, participants, states, activePersonaIds, pickSources, saveActive, updatePicks, setMentionUnlinkRequest],
  )

  const onMentionsChange = useCallback(
    (participantIds: Array<string>) => {
      const tagged = new Set(
        participants.filter((p) => participantIds.includes(p.id)).map((p) => p.personaId),
      )
      updatePicks((prev) => {
        const sources: Record<string, PickSource> = {}
        for (const [personaId, source] of Object.entries(prev.sources)) {
          if (source !== 'tag' || tagged.has(personaId)) {
            sources[personaId] = source
          }
        }
        for (const personaId of tagged) {
          if (!sources[personaId]) {
            sources[personaId] = 'tag'
          }
        }
        return { ...prev, sources }
      })
    },
    [participants, updatePicks],
  )

  const takeSendPicks = useCallback(() => {
    // Move the takeable picks to "sent, pending": the ring stays, the next send no longer carries them.
    const taken = orderByRoom(participants, new Set(Object.keys(readPicks().sources)))
    if (taken.length === 0) {
      lastTakeKeyRef.current = null
      return []
    }
    takeNonceRef.current += 1
    const takeKey = `take-${takeNonceRef.current}`
    lastTakeKeyRef.current = { roomToken, key: takeKey }
    // Keep on: the taken picks also stay takeable, re-armed as circle picks (a tag pick
    // would drop when the composer clears its text), so the next send carries them again.
    const isKeeping = isKeepOnRef.current
    updatePicks((prev) => {
      const pending = { ...prev.pending }
      for (const personaId of taken) {
        pending[personaId] = takeKey
      }
      let sources = withoutKeys(prev.sources, taken)
      if (isKeeping) {
        sources = { ...prev.sources }
        for (const personaId of taken) {
          sources[personaId] = 'circle'
        }
      }
      return {
        sources,
        pending,
        restoredFrom: withoutKeys(prev.restoredFrom, taken),
      }
    })
    if (isKeeping) {
      writeTellerKeep(roomToken, { isOn: true, personaIds: taken })
    }
    return taken
  }, [participants, readPicks, roomToken, updatePicks])

  const togglePick = useCallback(
    (personaId: string) => {
      if (!canPickTellers) {
        return
      }
      const participant = participants.find((p) => p.personaId === personaId)
      if (!participant) {
        return
      }
      const source = readPicks().sources[personaId]
      if (!source) {
        if (participant.enrichmentStatus !== 'ready') {
          return
        }
        updatePicks((prev) => ({ ...prev, sources: { ...prev.sources, [personaId]: 'circle' } }))
        return
      }
      if (source === 'tag') {
        unlinkNonceRef.current += 1
        setMentionUnlinkRequest({ participantId: participant.id, nonce: unlinkNonceRef.current })
      }
      updatePicks((prev) => ({
        ...prev,
        sources: withoutKeys(prev.sources, [personaId]),
        restoredFrom: withoutKeys(prev.restoredFrom, [personaId]),
      }))
      if (isKeepOnRef.current) {
        const kept = readTellerKeep(roomToken)
        if (kept.personaIds.includes(personaId)) {
          writeTellerKeep(roomToken, {
            isOn: true,
            personaIds: kept.personaIds.filter((id) => id !== personaId),
          })
        }
      }
    },
    [canPickTellers, participants, readPicks, updatePicks, setMentionUnlinkRequest, roomToken],
  )

  const setKeepOn = useCallback(
    (isOn: boolean) => {
      setKeepOverride({ roomToken, isOn })
      isKeepOnRef.current = isOn
      // Turning keep on keeps what is picked now; turning it off forgets the kept set.
      const current = orderByRoom(participants, new Set(Object.keys(readPicks().sources)))
      writeTellerKeep(roomToken, isOn ? { isOn: true, personaIds: current } : KEEP_OFF)
    },
    [roomToken, participants, readPicks],
  )

  const markSent = useCallback(
    (clientRequestId: string | undefined, sent: Array<string>) => {
      const take = lastTakeKeyRef.current
      lastTakeKeyRef.current = null
      const takeKey = take && take.roomToken === roomToken ? take.key : null
      if (!takeKey || sent.length === 0) {
        return
      }
      if (!clientRequestId) {
        // Refused before queueing (trial cap): nothing carries them, give them back.
        updatePicks((prev) => applySendOutcome(
          prev,
          { clientRequestId: takeKey, outcome: 'failed' },
        ))
        return
      }
      updatePicks((prev) => {
        const pending = { ...prev.pending }
        for (const personaId of sent) {
          if (pending[personaId] === takeKey) {
            pending[personaId] = clientRequestId
          }
        }
        return { ...prev, pending }
      })
    },
    [roomToken, updatePicks],
  )

  return {
    states,
    picks,
    bidderCount,
    isAllOff: bidderCount === 0,
    canUpdateSettings,
    canPickTellers,
    cycle,
    onMentionsChange,
    mentionUnlinkRequest,
    takeSendPicks,
    markSent,
    togglePick,
    isKeepOn,
    setKeepOn,
  }
}
